import {
  createBaseThemeOptions,
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
} from '@backstage/theme';

// ── Brand palette constants (Manual da Identidade Visual Spassu 2022) ─
const SPASSU_ORANGE = '#FA4616'; // Pantone 172 C – cor primária
const SPASSU_ORANGE_HOVER = '#E03D12';
const SPASSU_ORANGE_LIGHT = 'rgba(250, 70, 22, 0.06)';
const SPASSU_ORANGE_SUBTLE = 'rgba(250, 70, 22, 0.04)';
export const DARK_BLUELIGHT = '#201547'; // Pantone 275 C – cor secundária
export const AVOCATO = '#DBE442'; // Pantone 380 C – apenas Lexend Peta sobre Dark Bluelight
export const SPATIAL_GREY = '#D9D9D6'; // Pantone Cool Gray 1 C – textos decorativos

// ── Neutral scale ────────────────────────────────────────────────────
const NEUTRAL_950 = '#000000'; // Full Black (Manual da Marca)
const NEUTRAL_800 = '#1A1A1A';
const NEUTRAL_700 = '#333333';
const NEUTRAL_600 = '#555555';
const NEUTRAL_500 = '#777777';
const NEUTRAL_400 = '#999999';
const NEUTRAL_300 = '#BDBDBD';
const NEUTRAL_200 = '#D9D9D9';
const NEUTRAL_150 = '#E5E5E5';
const NEUTRAL_100 = '#F0F0F0';
const NEUTRAL_50 = '#F7F7F7';
const NEUTRAL_25 = '#FAFAFA';

export const spassuTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      primary: {
        main: NEUTRAL_950,
      },
      secondary: {
        main: SPASSU_ORANGE,
      },
      background: {
        default: NEUTRAL_50,
        paper: '#FFFFFF',
      },
      link: NEUTRAL_700,
      navigation: {
        background: '#FFFFFF',
        indicator: SPASSU_ORANGE,
        color: NEUTRAL_700,
        selectedColor: SPASSU_ORANGE,
        navItem: {
          hoverBackground: NEUTRAL_50,
        },
        submenu: {
          background: NEUTRAL_50,
        },
      },
    },
  }),
  defaultPageTheme: 'neutral',
  fontFamily: "'Inter', 'Poppins', sans-serif",
  pageTheme: {
    neutral: genPageTheme({
      colors: ['#FFFFFF', '#FFFFFF'],
      shape: shapes.wave,
      options: { fontColor: NEUTRAL_700 },
    }),
    home: genPageTheme({
      colors: ['#FFFFFF', '#FFFFFF'],
      shape: shapes.wave,
      options: { fontColor: NEUTRAL_700 },
    }),
    spassu: genPageTheme({
      colors: ['#FFFFFF', '#FFFFFF'],
      shape: shapes.wave,
      options: { fontColor: NEUTRAL_700 },
    }),
  },
  components: {
    // ── Global baseline ──────────────────────────────────────────
    MuiCssBaseline: {
      styleOverrides: {
        '@keyframes slideUp': {
          from: { opacity: 0, transform: 'translateY(16px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        html: {
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
          scrollBehavior: 'smooth',
        },
        body: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.875rem',
          lineHeight: 1.6,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
        '::-webkit-scrollbar': {
          width: 8,
          height: 8,
        },
        '::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '::-webkit-scrollbar-thumb': {
          backgroundColor: NEUTRAL_300,
          borderRadius: 4,
          border: '2px solid transparent',
          backgroundClip: 'padding-box',
        },
        '::-webkit-scrollbar-thumb:hover': {
          backgroundColor: NEUTRAL_400,
        },
      },
    },

    // ── Typography hierarchy ─────────────────────────────────────
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontFamily: "'Lexend Peta', 'Poppins', sans-serif",
          fontWeight: 700,
          fontSize: '2rem',
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
        },
        h2: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          fontSize: '1.5rem',
          letterSpacing: '-0.01em',
          lineHeight: 1.3,
        },
        h3: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          fontSize: '1.25rem',
          letterSpacing: '-0.005em',
          lineHeight: 1.35,
        },
        h4: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '1.125rem',
          lineHeight: 1.4,
        },
        h5: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '1rem',
          lineHeight: 1.4,
        },
        h6: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '0.875rem',
          lineHeight: 1.5,
        },
        subtitle1: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '0.875rem',
          letterSpacing: '0.01em',
        },
        subtitle2: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
        body1: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.875rem',
          lineHeight: 1.6,
        },
        body2: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.8125rem',
          lineHeight: 1.55,
        },
        caption: {
          fontFamily: "'Inter', sans-serif",
          fontWeight: 500,
          fontSize: '0.75rem',
          letterSpacing: '0.02em',
        },
        overline: {
          fontFamily: "'Inter', sans-serif",
          fontWeight: 600,
          fontSize: '0.6875rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase' as const,
        },
      },
    },

    // ── Backstage Header ─────────────────────────────────────────
    BackstageHeader: {
      styleOverrides: {
        header: {
          backgroundImage: 'none',
          backgroundColor: '#FFFFFF',
          borderBottom: `1px solid ${NEUTRAL_150}`,
          boxShadow: 'none',
          paddingBottom: 16,
        },
        title: {
          fontFamily: "'Lexend Peta', 'Poppins', sans-serif",
          fontWeight: 600,
          fontSize: '1.5rem',
          color: NEUTRAL_950,
          letterSpacing: '-0.01em',
        },
        subtitle: {
          fontFamily: "'Inter', sans-serif",
          fontWeight: 400,
          fontSize: '0.8125rem',
          color: NEUTRAL_500,
          letterSpacing: '0.01em',
        },
        type: {
          fontFamily: "'Inter', sans-serif",
          fontWeight: 600,
          fontSize: '0.6875rem',
          color: SPASSU_ORANGE,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.1em',
        },
        breadcrumbType: {
          fontFamily: "'Inter', sans-serif",
          fontWeight: 600,
          fontSize: '0.6875rem',
          color: SPASSU_ORANGE,
          textTransform: 'uppercase' as const,
          letterSpacing: '0.1em',
        },
        breadcrumbTitle: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '0.8125rem',
          color: NEUTRAL_600,
        },
      },
    },
    BackstageHeaderTabs: {
      styleOverrides: {
        tabsWrapper: {
          backgroundColor: '#FFFFFF',
          borderBottom: `1px solid ${NEUTRAL_150}`,
        },
        defaultTab: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '0.8125rem',
          textTransform: 'none' as const,
          letterSpacing: '0.01em',
          color: NEUTRAL_600,
          padding: '12px 20px',
          transition: 'color 0.2s ease',
          '&:hover': {
            color: NEUTRAL_950,
            backgroundColor: NEUTRAL_25,
          },
        },
        selected: {
          color: SPASSU_ORANGE,
          fontWeight: 600,
        },
      },
    },

    // ── Backstage Content ────────────────────────────────────────
    BackstageContent: {
      styleOverrides: {
        root: {
          padding: '24px 32px',
        },
      },
    },
    BackstageContentHeader: {
      styleOverrides: {
        title: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          fontSize: '1.125rem',
          color: NEUTRAL_800,
        },
      },
    },

    // ── Backstage Sidebar ────────────────────────────────────────
    BackstageSidebar: {
      styleOverrides: {
        drawer: {
          borderRight: `1px solid ${NEUTRAL_150}`,
          boxShadow: 'none',
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
        buttonItem: {
          borderRadius: 8,
          margin: '2px 8px',
          padding: '8px 12px',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: NEUTRAL_50,
            transform: 'translateX(2px)',
          },
        },
        selected: {
          color: `${SPASSU_ORANGE} !important`,
          fontWeight: 600,
          backgroundColor: `${SPASSU_ORANGE_SUBTLE} !important`,
        },
        label: {
          fontFamily: "'Poppins', sans-serif",
          fontSize: '0.8125rem',
          // O Backstage fixa o rótulo em 110px com `nowrap` + reticências.
          // Itens como "Controle de Acesso" não cabem nessa largura; deixamos
          // o texto ocupar o espaço disponível e quebrar em duas linhas.
          whiteSpace: 'normal',
          width: 'auto',
          lineHeight: 1.25,
        },
      },
    },
    BackstageSidebarDivider: {
      styleOverrides: {
        root: {
          background: NEUTRAL_150,
          margin: '8px 16px',
        },
      },
    },

    // ── Backstage InfoCard ───────────────────────────────────────
    BackstageInfoCard: {
      styleOverrides: {
        noPadding: {
          borderTop: `3px solid ${SPASSU_ORANGE}`,
        },
        header: {
          borderBottom: `1px solid ${NEUTRAL_100}`,
          paddingBottom: 12,
        },
        headerTitle: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          fontSize: '0.9375rem',
          color: NEUTRAL_800,
        },
        headerSubheader: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.8125rem',
          color: NEUTRAL_500,
        },
      },
    },

    // ── Backstage Template Cards ─────────────────────────────────
    BackstageItemCardHeader: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: NEUTRAL_50,
          borderBottom: `1px solid ${NEUTRAL_100}`,
          padding: '16px 20px',
        },
      },
    },
    BackstageItemCardGrid: {
      styleOverrides: {
        root: {
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 20,
        },
      },
    },

    // ── Paper & Card ─────────────────────────────────────────────
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'unset',
        },
        rounded: {
          borderRadius: 12,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
          border: `1px solid ${NEUTRAL_100}`,
        },
        elevation2: {
          boxShadow: '0 2px 6px rgba(0,0,0,0.05), 0 1px 3px rgba(0,0,0,0.03)',
          border: `1px solid ${NEUTRAL_100}`,
        },
        elevation3: {
          boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.03)',
          border: `1px solid ${NEUTRAL_100}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: `1px solid ${NEUTRAL_100}`,
          borderTop: `3px solid ${SPASSU_ORANGE}`,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
          transition:
            'box-shadow 0.25s ease, border-color 0.25s ease, transform 0.25s ease',
          display: 'flex',
          flexDirection: 'column' as const,
          '&:hover': {
            boxShadow:
              '0 8px 24px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.03)',
            borderColor: NEUTRAL_150,
            borderTopColor: SPASSU_ORANGE,
            transform: 'translateY(-1px)',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          paddingBottom: 0,
        },
        title: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          fontSize: '0.9375rem',
        },
        subheader: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.8125rem',
          color: NEUTRAL_500,
          marginTop: 2,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          flexGrow: 1,
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          borderTop: `1px solid ${NEUTRAL_100}`,
          padding: '12px 16px',
          '& a': {
            color: SPASSU_ORANGE,
            fontWeight: 500,
            fontSize: '0.8125rem',
          },
        },
      },
    },

    // ── Tables ───────────────────────────────────────────────────
    MuiTableCell: {
      styleOverrides: {
        head: {
          whiteSpace: 'nowrap',
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          fontSize: '0.6875rem',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.08em',
          color: NEUTRAL_500,
          backgroundColor: NEUTRAL_50,
          borderBottom: `2px solid ${NEUTRAL_150}`,
          paddingTop: 14,
          paddingBottom: 14,
          paddingLeft: 20,
          paddingRight: 20,
        },
        body: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.8125rem',
          color: NEUTRAL_700,
          borderBottomColor: NEUTRAL_100,
          paddingTop: 14,
          paddingBottom: 14,
          paddingLeft: 20,
          paddingRight: 20,
          '& a': {
            fontWeight: 500,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
          '&:hover': {
            backgroundColor: `${NEUTRAL_25} !important`,
            boxShadow: `inset 3px 0 0 0 ${SPASSU_ORANGE}`,
          },
          '&:last-child td': {
            borderBottom: 'none',
          },
        },
        head: {
          '&:hover': {
            backgroundColor: `${NEUTRAL_50} !important`,
            boxShadow: 'none',
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: 'inherit',
          '&:hover': { color: SPASSU_ORANGE },
          '&:focus': { color: 'inherit' },
          '&.Mui-active': {
            fontWeight: 700,
            color: SPASSU_ORANGE,
            '& .MuiTableSortLabel-icon': {
              color: `${SPASSU_ORANGE} !important`,
            },
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'color 0.15s ease, background-color 0.15s ease',
          '&:hover': {
            color: SPASSU_ORANGE,
            backgroundColor: SPASSU_ORANGE_SUBTLE,
          },
        },
      },
    },

    // ── Buttons ──────────────────────────────────────────────────
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none' as const,
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '0.8125rem',
          letterSpacing: '0.01em',
          transition: 'all 0.2s ease',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          },
          '&:active': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: SPASSU_ORANGE,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: SPASSU_ORANGE_HOVER,
            boxShadow: '0 4px 12px rgba(250, 70, 22, 0.3)',
          },
        },
        outlined: {
          borderColor: NEUTRAL_200,
          color: NEUTRAL_700,
          '&:hover': {
            borderColor: SPASSU_ORANGE,
            color: SPASSU_ORANGE,
            backgroundColor: SPASSU_ORANGE_SUBTLE,
          },
        },
        text: {
          color: NEUTRAL_700,
          '&:hover': {
            backgroundColor: NEUTRAL_50,
          },
        },
      },
    },

    // ── Chips ────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '0.75rem',
          height: 26,
          transition: 'all 0.15s ease',
          '&:hover': {
            transform: 'scale(1.03)',
          },
        },
        label: {
          lineHeight: 1,
          paddingLeft: 10,
          paddingRight: 10,
        },
        outlined: {
          borderColor: NEUTRAL_200,
          '&:hover': {
            borderColor: SPASSU_ORANGE,
            backgroundColor: SPASSU_ORANGE_SUBTLE,
          },
        },
        colorPrimary: {
          backgroundColor: SPASSU_ORANGE_LIGHT,
          color: SPASSU_ORANGE,
          border: 'none',
        },
      },
    },

    // ── Tabs ─────────────────────────────────────────────────────
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 500,
          fontSize: '0.8125rem',
          letterSpacing: '0.01em',
          minHeight: 40,
          transition: 'color 0.2s ease',
          '&:hover': {
            color: SPASSU_ORANGE,
            backgroundColor: SPASSU_ORANGE_SUBTLE,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: SPASSU_ORANGE,
          height: 2,
          borderRadius: '2px 2px 0 0',
        },
      },
    },

    // ── Inputs ───────────────────────────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.875rem',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: NEUTRAL_300,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: SPASSU_ORANGE,
            borderWidth: 1,
            boxShadow: `0 0 0 3px ${SPASSU_ORANGE_SUBTLE}`,
          },
        },
        notchedOutline: {
          borderColor: NEUTRAL_150,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: NEUTRAL_500,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.8125rem',
        },
      },
    },

    // ── Menu items ───────────────────────────────────────────────
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.8125rem',
          transition: 'background-color 0.15s ease, color 0.15s ease',
          '&:hover': {
            backgroundColor: NEUTRAL_50,
          },
          '&.Mui-selected': {
            backgroundColor: SPASSU_ORANGE_SUBTLE,
            '&:hover': {
              backgroundColor: SPASSU_ORANGE_LIGHT,
            },
          },
        },
      },
    },

    // ── Dialogs ──────────────────────────────────────────────────
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(2px)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow:
            '0 24px 48px rgba(0,0,0,0.12), 0 8px 16px rgba(0,0,0,0.08)',
          animation: 'slideUp 0.25s ease-out',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 600,
          fontSize: '1.125rem',
          padding: '20px 24px 12px 24px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '8px 24px 16px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '12px 24px 20px 24px',
        },
      },
    },

    // ── Tooltips ─────────────────────────────────────────────────
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 6,
          backgroundColor: NEUTRAL_800,
          padding: '6px 12px',
          animation: 'fadeIn 0.15s ease-out',
        },
      },
    },

    // ── Links ────────────────────────────────────────────────────
    MuiLink: {
      styleOverrides: {
        root: {
          color: SPASSU_ORANGE,
          textDecoration: 'none',
          fontWeight: 500,
          borderBottom: '1.5px solid transparent',
          transition: 'color 0.15s ease, border-bottom-color 0.2s ease',
          '&:hover': {
            color: SPASSU_ORANGE_HOVER,
            textDecoration: 'none',
            borderBottomColor: SPASSU_ORANGE,
          },
        },
      },
    },

    // ── Alerts ───────────────────────────────────────────────────
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: "'Inter', sans-serif",
          fontSize: '0.8125rem',
        },
        standardSuccess: {
          backgroundColor: 'rgba(219, 228, 66, 0.12)',
          color: '#5A6100',
        },
        standardWarning: {
          backgroundColor: SPASSU_ORANGE_LIGHT,
          color: SPASSU_ORANGE_HOVER,
        },
      },
    },

    // ── Progress bars ────────────────────────────────────────────
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 3,
          backgroundColor: NEUTRAL_100,
        },
        barColorPrimary: {
          backgroundColor: SPASSU_ORANGE,
          borderRadius: 4,
        },
      },
    },
  },
});
